import { KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  IVisitedPage,
  getVisitedTrail,
  popVisitedPagesTo,
} from '../../../helpers/navigationHistory'
import Icon from '../Icon'
import IconButton from '../IconButton'
import Menu, { OpenMenu } from '../Menu'
import MenuItem from '../MenuItem'
import styles from './BackTrail.module.scss'

/** Deep enough to find the page you want, short enough to read at a glance. */
const VISIBLE_TRAIL_ENTRIES = 5

const toHref = ({ pathname, search, hash }: IVisitedPage): string =>
  `${pathname}${search ?? ''}${hash ?? ''}`

/**
 * The way back, for a PWA that has no browser back button.
 *
 * Clicking goes back one page; hovering (or tabbing to it) opens the recent trail,
 * so a user several pages deep can jump straight to the one they want instead of
 * clicking back repeatedly. Renders nothing at all when there is nowhere to go —
 * a deep link or a fresh tab gets no dead control.
 */
const BackTrail = () => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [closeMenuCount, setCloseMenuCount] = useState(0)

  // Re-read per navigation rather than per render: the trail lives in
  // sessionStorage, and the location change is the only thing that moves it.
  const trail = useMemo(
    () => getVisitedTrail(pathname).slice(0, VISIBLE_TRAIL_ENTRIES),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, search],
  )

  // Unwind the trail to the target rather than letting the arrival record a fresh visit,
  // which would put the page being left back on top and make the next click return to it.
  const goTo = useCallback(
    (page: IVisitedPage) => {
      setCloseMenuCount((count) => count + 1)
      popVisitedPagesTo(page)
      void navigate(toHref(page))
    },
    [navigate],
  )

  const handleBack = useCallback(() => {
    if (trail[0]) {
      goTo(trail[0])
    }
  }, [goTo, trail])

  // The keyboard half of a menu button: ArrowDown opens the trail and steps into it,
  // Escape dismisses. Opening on focus instead looks equivalent and is not — MUI hands
  // focus back to the trigger on close, which is focus-visible, which reopens the menu.
  const handleKeyDown = useCallback(
    (open: OpenMenu, isOpen: boolean) => (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        open(event, { withFocus: true })
        return
      }
      if (event.key === 'Escape' && isOpen) {
        setCloseMenuCount((count) => count + 1)
      }
    },
    [],
  )

  const handleSelect = useCallback((page: IVisitedPage) => () => goTo(page), [goTo])

  if (!trail.length) {
    return null
  }

  return (
    <Menu
      closeMenuCount={closeMenuCount}
      isHoverMenu
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      renderTrigger={(open, isOpen) => (
        <IconButton
          title={`Back to ${trail[0].title}`}
          // The trail menu already opens on hover; a tooltip on the same gesture would
          // fight it. `title` still supplies the accessible name.
          disableTooltip
          className={styles.trigger}
          size="small"
          data-cy="back-trail-button"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          onClick={handleBack}
          onMouseEnter={open}
          onKeyDown={handleKeyDown(open, isOpen)}
        >
          <Icon name="chevron-left" size="sm" />
        </IconButton>
      )}
    >
      {trail.map((page) => (
        <MenuItem
          key={toHref(page)}
          onClick={handleSelect(page)}
          className={styles.item}
          casing="none"
        >
          {page.title}
        </MenuItem>
      ))}
    </Menu>
  )
}

export default BackTrail
